/**
 * Flujo único de logout — centraliza audit + limpieza del store + navegación.
 *
 * Fix R1 (auditoría GAMP 5): el audit `logout` se hacía desde Header/Sidebar
 * (call-sites duplicados) y no siempre se ejecutaba — si la sesión moría
 * fuera del botón quedaba un `login` huérfano sin `logout` correspondiente.
 *
 * Fix R4 (auditoría GAMP 5): tras logout limpiamos el cache de TanStack
 * Query — sin esto el siguiente login arrancaba con datos en memoria del
 * usuario anterior.
 *
 * Fix Q4 (calidad código): `handleLogout` estaba duplicado idéntico en
 * Sidebar.tsx y Header.tsx — ahora viven un solo flujo.
 *
 * Deuda conocida: los `setTimeout(2000)` del SNT en endpoints mock no se
 * cancelan (no están tracked en un AbortController). En producción los
 * timers viven server-side y la sesión vencida invalida el callback.
 */

import type { QueryClient } from '@tanstack/react-query';
import { useAuditStore } from '@/stores/audit';
import { useSessionStore } from '@/stores/session';

export interface PerformLogoutOptions {
  /** `useNavigate()` de react-router-dom — pasado por el caller (hook). */
  navigate: (to: string, opts?: { replace?: boolean }) => void | Promise<void>;
  /** `useQueryClient()` de TanStack — pasado por el caller. */
  queryClient: QueryClient;
  /** Razón opcional (vacío si el usuario clickea el botón). */
  razon?: string;
}

/**
 * Ejecuta el ciclo completo:
 *   1. Audita `logout` con el usuario activo (antes de limpiar la sesión).
 *   2. Llama al store.logout() (que también pega al endpoint mock).
 *   3. Limpia el cache de TanStack Query.
 *   4. Navega a /login.
 */
export async function performLogout(opts: PerformLogoutOptions): Promise<void> {
  const { navigate, queryClient, razon } = opts;
  // 1. Audit ANTES de limpiar la sesión (así queda el usuarioId atribuido)
  useAuditStore.getState().addEntry({
    categoria: 'sesion',
    accion: 'logout',
    razon: razon ?? 'Cierre de sesión desde la UI',
  });
  // 2. Limpiar sesión + pegar al endpoint mock
  await useSessionStore.getState().logout();
  // 3. Limpiar cache (R4) — evita que el siguiente login vea data del anterior
  queryClient.clear();
  // 4. Navegar al login
  void navigate('/login', { replace: true });
}
